import React, { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
} from 'recharts';
import { Card, CardContent, Typography, Box, Button } from '@mui/material';
import { getTop3Submarkets, wrapLabel } from './helper';
import {
  getGradeColor,
  formatCurrency,
  abbreviateNumber,
  convertCurrency,
} from 'src/utils/functions';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_1,
  COLOR_SUCCESS,
  COLOR_WARNING,
  COLOR_ERROR,
  COLOR_GREY_200,
  COLOR_GREY_600,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
  COLOR_BACKGROUND_1,
  COLOR_BACKGROUND_2,
  COLOR_GREY_400,
  COLOR_PRIMARY_1_700,
} from 'src/utils/colors';
import { exchangeRates, LOCKED_TOP_SUBMARKET_GRAPH_STATS } from 'src/utils/const';
import { useSelector } from 'react-redux';
import SubmarketTooltip from './SubmarketTooltip';
import LockIcon from '@mui/icons-material/Lock';
import { styles } from './styles';

interface TopSubmarketGraphProps {
  submarkets: any;
  marketDisplayName: string;
  handleOnSampleClick: (plan: string) => void;
  isSubmarketAccessAllowed: boolean;
  setDashboardLayoutView: (view: string) => void;
  parentMarketDetails?: any;
  submarketParentMarketPerformance: any;
  isMobileView: boolean;
  top3SubmarketsRef?: any;
  topSubMarketChartRef?: any;
}

interface ChartDataPoint {
  name: string;
  revenue: number;
  adr: number;
  supply: number;
  occupancy_rate: number;
  neighborhood_grade: string | null;
  isAverage?: boolean;
  color: string;
  rank: number | null;
  submarket_name: string;
  latitude: number | null;
  longitude: number | null;
}

const TopSubmarketGraph = ({
  submarkets,
  marketDisplayName,
  handleOnSampleClick,
  isSubmarketAccessAllowed,
  setDashboardLayoutView,
  parentMarketDetails,
  submarketParentMarketPerformance,
  isMobileView,
  top3SubmarketsRef,
  topSubMarketChartRef
}: TopSubmarketGraphProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const top3Submarkets = getTop3Submarkets(submarkets);

  const currency = useSelector((state: any) => state.dashboard.currency);

  const parentMarketName = `${parentMarketDetails?.displayName ||
    parentMarketDetails?.district ||
    marketDisplayName}`;

  // Prepare chart data
  const chartData: ChartDataPoint[] = useMemo(() => {
    const data: ChartDataPoint[] = top3Submarkets.map((submarket, index) => ({
      name: submarket.submarket_name,
      revenue: isSubmarketAccessAllowed
        ? submarket.revenue
        : LOCKED_TOP_SUBMARKET_GRAPH_STATS[index].revenue,
      adr: isSubmarketAccessAllowed ? submarket.adr : LOCKED_TOP_SUBMARKET_GRAPH_STATS[index].adr,
      supply: submarket.supply,
      occupancy_rate: isSubmarketAccessAllowed
        ? submarket.occupancy_rate
        : LOCKED_TOP_SUBMARKET_GRAPH_STATS[index].occupancy_rate,
      neighborhood_grade: isSubmarketAccessAllowed
        ? submarket.neighborhood_grade
        : LOCKED_TOP_SUBMARKET_GRAPH_STATS[index].neighborhood_grade,
      color: COLOR_PRIMARY_1,
      submarket_name: submarket.submarket_name + ' (Submarket)',
      rank: isSubmarketAccessAllowed
        ? submarket.rank
        : LOCKED_TOP_SUBMARKET_GRAPH_STATS.find(stat => stat.rank === submarket.rank)?.rank,
        latitude: submarket.center.lat,
        longitude: submarket.center.lng,
    }));

    // Add Dubai average as reference
    submarketParentMarketPerformance &&
      data.push({
        name: `${parentMarketName} median`,
        submarket_name: `${parentMarketName} (Market)`,
        revenue: isSubmarketAccessAllowed
          ? submarketParentMarketPerformance.revenue
          : LOCKED_TOP_SUBMARKET_GRAPH_STATS[3].revenue,
        adr: isSubmarketAccessAllowed
          ? submarketParentMarketPerformance.adr
          : LOCKED_TOP_SUBMARKET_GRAPH_STATS[3].adr,
        supply: submarketParentMarketPerformance.supply,
        occupancy_rate: isSubmarketAccessAllowed
          ? submarketParentMarketPerformance.occupancy_rate
          : LOCKED_TOP_SUBMARKET_GRAPH_STATS[3].occupancy_rate,
        neighborhood_grade: null,
        isAverage: true,
        color: COLOR_GREY_600,
        rank: null,
        latitude: null,
        longitude: null,
      });

      if(top3SubmarketsRef){
        top3SubmarketsRef.current.chartData = data;
      }

    return data;
  }, [top3Submarkets, submarketParentMarketPerformance, isSubmarketAccessAllowed]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload as ChartDataPoint;

    const isAverage = data.isAverage;

    return (
      <>
        <SubmarketTooltip
          returnTooltipBodyOnly={true}
          title={''}
          content={''}
          children={<></>}
          submarketData={data}
          handleOnSampleClick={handleOnSampleClick}
          showCta={false}
          disable={false}
          handleHideSubmarket={() => {}}
          isSubmarketAccessAllowed={isSubmarketAccessAllowed}
          setDashboardLayoutView={() => {}}
          hideMarketGrade={isAverage}
          marketGradeSx={{ transform: 'translateY(-220px)' }}
          blurSubmarketName={!isSubmarketAccessAllowed && !isAverage}
        />
      </>
    );
  };

  return (
    <Card
      sx={{
        backgroundColor: COLOR_BACKGROUND_2,
        borderRadius: 3,
        overflow: 'visible',
        boxShadow: 'none',
        width: '100%',
        py: '0 !important',
      }}
    >
      <CardContent
        sx={{
          p: isMobileView ? 0 : 1,
          py: '0 !important',
          boxShadow: 'none',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: COLOR_TEXT_1,
                mb: 0.5,
              }}
            >
              Top {parentMarketName} submarkets vs {parentMarketName} median performance
            </Typography>
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ height: 300, mb: 0 }} ref={topSubMarketChartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              onMouseMove={state => {
                if (state && state.isTooltipActive !== undefined) {
                  setHoveredIndex(state.activeTooltipIndex);
                }
              }}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={COLOR_GREY_400} vertical={false} />

              <YAxis
                tickLine={false}
                axisLine={{
                  stroke: COLOR_TEXT_2,
                  strokeDasharray: '4 4',
                }}
                tick={props => {
                  const { x, y, payload } = props;
                  const value = abbreviateNumber(convertCurrency(currency, payload.value), false);                  

                  return (
                    <text
                      x={x}
                      y={y}
                      dy={4}
                      fontSize={12}
                      fontWeight={500}
                      fill={COLOR_TEXT_2}
                      textAnchor="end"
                    >
                      {exchangeRates[currency].symbol}
                      <tspan style={{ filter: !isSubmarketAccessAllowed ? 'blur(5px)' : 'none' }}>
                        {value}
                      </tspan>
                    </text>
                  );
                }}
                label={{
                  value: 'Revenue',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    textAnchor: 'middle',
                    fontSize: 12,
                    fontWeight: 600,
                    fill: COLOR_TEXT_2,
                  },
                }}
              />

              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                height={80}
                interval={0}
                tick={props => {
                  const { x, y, payload } = props;
                  const lines = wrapLabel(String(payload.value), 12);
                  
                  const chartDataPoint = chartData[payload.index];

                  return (
                    <text
                      x={x}
                      y={y + 10}
                      textAnchor="end"
                      fontSize={12}
                      fontWeight={500}
                      fill={COLOR_TEXT_2}
                      transform={`rotate(-45, ${x}, ${y + 10})`}
                    >
                      {lines.map((line, i) => (
                        <tspan
                          key={i}
                          x={x} 
                          dy={i === 0 ? 0 : 14}
                          style={{
                            filter:
                              !isSubmarketAccessAllowed && !chartDataPoint?.isAverage ? 'blur(5px)' : 'none',
                          }}
                        >
                          {line}
                        </tspan>
                      ))}
                    </text>
                  );
                }}
              />

              {/* Next to sub market -> add (add submarkt text) similar for market */}

              <Tooltip
                content={<CustomTooltip />}
                cursor={false}
                interactive={true}
                wrapperStyle={{ pointerEvents: 'auto' }}
              />

              <Bar
                dataKey="revenue"
                radius={[4, 4, 0, 0]}
                barSize={isMobileView ? 20 : 40}
                animationDuration={1000}
                animationBegin={0}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.isAverage ? COLOR_PRIMARY_1 : COLOR_PRIMARY_2}
                  />
                ))}
              </Bar>
              {submarketParentMarketPerformance && (
                <ReferenceLine
                  y={
                    isSubmarketAccessAllowed
                      ? submarketParentMarketPerformance.revenue
                      : LOCKED_TOP_SUBMARKET_GRAPH_STATS[3].revenue
                  }
                  stroke={COLOR_SECONDARY_1}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: `${parentMarketName} median`,
                    position: 'top',
                    fill: COLOR_SECONDARY_1,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
          <Button
            variant="contained"
            onClick={() => setDashboardLayoutView('submarkets')}
            sx={{ ...styles.ctaButton, float: 'right', mr: -8, mt: -2, mb: 2 }}
          >
            Compare Submarkets
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TopSubmarketGraph;
